import React, { useState } from "react";
import { Paper, Box, Typography, Grid } from "@mui/material";
import SelectSkills from "../../../components/FormComponents/SelectSkills";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import FormButtonLayout from "../../../components/LayoutComponents/FormButtonLayout";
import RadioSelect from "../../../components/FormComponents/RadioSelect";
import CustomDropdown from "../../../components/FormComponents/CustomDropdown";
import { requestCourseTypes } from "../../../remote/courseRequest";
import { requestAssessmentMethods } from "../../../remote/assessmentMethodRequests";
import { requestDeliveryFormats } from "../../../remote/deliveryFormatRequests"
import CustomDatePicker from "../../../components/FormComponents/CustomDatePicker";
import dayjs from "dayjs";
import CustomTextField from "../../../components/FormComponents/CustomTextField";


export default function CourseForm({ onSave, onDelete, session, formData, setFormData, loading, competencies, proficiencies, skills, setSkills, companies }) {

    const [showSkillsPage, setShowSkillsPage] = useState(false)
    const [error, setError] = useState({});

    const handleSave = () => {
        let errors = {}


        if (!formData?.courseName || formData.courseName.trim() === "") {
            errors.courseName = "Course Name is required";
        }

        if (!formData?.courseCode || formData.courseCode.trim() === "") {
            errors.courseCode = "Course Code is required";
        }

        if (!formData?.courseDescription || formData.courseDescription.trim() === "") {
            errors.courseDescription = "Course Description is required";
        }

        if (!formData.effectiveStartDate) {
            errors.effectiveStartDate = "Effective Start Date is required."
        }
        if (formData?.effectiveStartDate && !formData?.effectiveEndDate) {
            errors.effectiveEndDate = "Effective End Date is required when a start date is chosen.";
        }
        if (
            formData?.effectiveStartDate &&
            formData?.effectiveEndDate &&
            new Date(formData.effectiveStartDate) > new Date(formData.effectiveEndDate)
        ) {
            errors.effectiveEndDate = "Effective End Date cannot be before start date.";
        }
        if (!formData?.courseRequirements || formData.courseRequirements.trim() === "") {
            errors.courseRequirements = "Course Requirements is required";
        }


        if (Object.keys(errors).length > 0) {
            setError(errors);
            return;
        }

        setError({});
        onSave();
    }

    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };



    return (
        <>
            {showSkillsPage ? (
                <SelectSkills
                    competencies={competencies}
                    proficiencies={proficiencies}
                    selected={skills}
                    setSelected={setSkills}
                    setShowSkillsPage={setShowSkillsPage}
                    label={formData?.courseName || ""}
                    loading={loading}
                    error={Object.keys(error).length > 0}
                />
            ) : (<Paper sx={{ p: 3, display: "flex", flexDirection: "column", maxHeight: "60vh", overflowY: "auto" }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                        Course Details
                    </Typography>
                    {loading && <LoadingCircle size={20} />}
                </Box>

                <Grid container spacing={2}>
                    {/* Course Name */}
                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            formData={formData}
                            field="courseName"
                            handleChange={handleChange}
                            label="Course Name"
                            required
                            error={!!error.courseName}
                            helperText={error.courseName}
                        />
                    </Grid>

                    {/* Course Code */}
                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            formData={formData}
                            field="courseCode"
                            handleChange={handleChange}
                            label="Course Code"
                            required
                            error={!!error.courseCode}
                            helperText={error.courseCode}
                        />
                    </Grid>

                    {/* Course Description */}
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field="courseDescription"
                            handleChange={handleChange}
                            label="Course Description"
                            multiline
                            rows={4}
                            required
                            error={!!error.courseDescription}
                            helperText={error.courseDescription}
                            variant="outlined"
                        />
                    </Grid>

                    {/* RadioSelects */}
                    <Grid item xs={12} sm={6}>
                        <RadioSelect
                            value={formData?.isActive}
                            label={formData.isActive ? "Active" : "Not Active"}
                            onChange={(val) => handleChange("isActive", val)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <RadioSelect
                            value={formData?.featured}
                            label={formData.featured ? "Featured" : "Not Featured"}
                            onChange={(val) => handleChange("featured", val)}
                        />
                    </Grid>

                    {/* Dates */}
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            selectedDate={dayjs(formData?.effectiveStartDate)}
                            setSelectedDate={(value) => handleChange("effectiveStartDate", value)}
                            label="Effective Start Date"
                            error={!!error.effectiveStartDate}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            selectedDate={dayjs(formData?.effectiveEndDate)}
                            setSelectedDate={(value) => handleChange("effectiveEndDate", value)}
                            label="Effective End Date"
                            error={!!error.effectiveEndDate}
                        />
                    </Grid>

                    {/* Other Text Fields */}
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field="courseOutline"
                            handleChange={handleChange}
                            label="Course Outline"
                            multiline
                            rows={4}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field="courseRequirements"
                            handleChange={handleChange}
                            label="Course Requirements"
                            multiline
                            rows={4}
                            required
                            error={!!error.courseRequirements}
                            helperText={error.courseRequirements}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field="learningObjectives"
                            handleChange={handleChange}
                            label="Learning Objectives"
                            multiline
                            rows={4}
                            variant="outlined"
                        />
                    </Grid>

                    {/* Numeric Fields */}
                    {['clinicalHours', 'contactHours', 'creditHours', 'lectureHours'].map((field) => (
                        <Grid item xs={12} sm={6} key={field}>
                            <CustomTextField
                                formData={formData}
                                field={field}
                                handleChange={handleChange}
                                label={field
                                    .replace(/([A-Z])/g, ' $1')
                                    .replace(/^./, str => str.toUpperCase())}
                                type="number"
                                error={!!error[field]}
                                helperText={error[field]}
                            />
                        </Grid>
                    ))}

                    {/* Dropdowns */}
                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            value={formData?.courseTypeId}
                            entityName="courseTypeName"
                            entityIdField="courseTypeID"
                            label="Course Type"
                            setEntity={(id) => handleChange("courseTypeId", id)}
                            error={!!error.courseTypeId}
                            helperText={error.courseTypeId}
                            request={requestCourseTypes}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            value={formData?.assessmentMethodId}
                            entityName="assessmentMethodName"
                            entityIdField="assessmentMethodID"
                            label="Assessment Method"
                            setEntity={(id) => handleChange("assessmentMethodId", id)}
                            error={!!error.assessmentMethodId}
                            helperText={error.assessmentMethodId}
                            request={requestAssessmentMethods}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            value={formData?.deliveryFormatId}
                            entityName="deliveryFormatName"
                            entityIdField="deliveryFormatID"
                            label="Delivery Format"
                            setEntity={(id) => handleChange("deliveryFormatId", id)}
                            error={!!error.deliveryFormatId}
                            helperText={error.deliveryFormatId}
                            request={requestDeliveryFormats}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            value={formData?.entityId}
                            entityName="entityName"
                            entityIdField="entityId"
                            label="Education Facility"
                            setEntity={(id) => handleChange("entityId", id)}
                            helperText={error.entityId}
                            request={async () => companies}
                        />
                    </Grid>
                </Grid>

            </Paper>
            )}
            <Box sx={{ mt: 2 }}>
                <FormButtonLayout
                    handleSave={handleSave}
                    onDelete={onDelete}
                    showSkillsPage={showSkillsPage}
                    setShowSkillsPage={setShowSkillsPage}
                    selected={formData?.courseId > 0}
                />
            </Box>

        </>
    )
}